// src/pages/ai/AssistantDesigner.tsx
import { useMemo, useState } from 'react';
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { 
  Sparkles, 
  Download, 
  Copy, 
  ImageIcon,
  Boxes,
  Layers,
  Eye
} from "lucide-react";
import { lazy, Suspense } from 'react';

const ThreePreview = lazy(() => import('./components/ThreePreview').then(m => ({ default: m.ThreePreview })));

type Preset = 'Nowoczesny' | 'Industrialny' | 'Minimalistyczny';
type Mode = '3D' | '2D' | 'AR';

export default function AssistantDesigner() {
  const [mode, setMode] = useState<Mode>('3D');
  const [preset, setPreset] = useState<Preset>('Nowoczesny');

  const [type, setType] = useState('Pojemnik / Organizer');
  const [material, setMaterial] = useState('Plexi bezbarwna');
  const [color, setColor] = useState('Transparentny');

  const [width, setWidth] = useState(400);
  const [height, setHeight] = useState(300);
  const [depth, setDepth] = useState(200);
  const [thickness, setThickness] = useState(3);
  const [withLid, setWithLid] = useState(true);

  const dims = useMemo(() => ({ width, height, depth, thickness }), [width, height, depth, thickness]);

  return (
    <div className="mx-auto max-w-[1400px] p-6 md:p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">
          Asystent Projektowy AI
        </h1>
        <div className="text-sm text-gray-400">Wizualizuj produkty z plexi</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Panel ustawień */}
        <Card className="lg:col-span-4 backdrop-blur bg-gray-900/70 border-gray-800">
          <div className="p-4 border-b border-gray-800">
            <div className="text-lg font-medium text-white">Ustawienia</div>
          </div>
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 gap-3">

              <FormSelect 
                label="Rodzaj" 
                value={type} 
                onChange={setType} 
                items={[
                  'Formatka / Płyta',
                  'Pojemnik / Organizer',
                  'Kaseton reklamowy',
                  'Ekspozytor',
                  'Ledon z plexi',
                  'Gablota',
                  'Obudowa urządzenia',
                  'Impuls przykasowy'
                ]}
              />

              <div className="grid grid-cols-2 gap-3">
                <FormSelect 
                  label="Materiał" 
                  value={material} 
                  onChange={setMaterial} 
                  items={[
                    'Plexi bezbarwna',
                    'Plexi dymiona',
                    'Plexi mleczna',
                    'Kolor'
                  ]}
                />
                <FormSelect 
                  label="Kolor" 
                  value={color} 
                  onChange={setColor} 
                  items={[
                    'Transparentny',
                    'Biały',
                    'Czarny'
                  ]}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <FormNumber label="Szerokość (mm)" value={width} onChange={setWidth}/>
                <FormNumber label="Wysokość (mm)" value={height} onChange={setHeight}/>
                <FormNumber label="Głębokość (mm)" value={depth} onChange={setDepth}/>
              </div>
              <FormNumber label="Grubość (mm)" value={thickness} onChange={setThickness}/>

              {type.includes('Pojemnik') && (
                <div className="flex items-center justify-between rounded-xl border border-gray-800 p-3 bg-gray-800/50">
                  <div className="text-sm text-gray-200">Pokrywka</div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={withLid} 
                      onChange={(e) => setWithLid(e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              )}

              {/* Presety */}
              <div className="space-y-2">
                <div className="text-xs text-gray-400">Presety wyglądu</div>
                <div className="flex gap-2">
                  {(['Nowoczesny','Industrialny','Minimalistyczny'] as Preset[]).map(p => (
                    <button 
                      key={p}
                      onClick={() => setPreset(p)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all
                        ${preset===p 
                          ? 'bg-blue-600 text-white border-blue-500' 
                          : 'border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-gray-200'}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <Button className="w-full gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                <Sparkles className="h-4 w-4" />
                Wygeneruj
              </Button>
            </div>
          </div>
        </Card>

        {/* Podgląd i wyniki */}
        <div className="lg:col-span-8 space-y-6">
          <Card className="backdrop-blur bg-gray-900/70 border-gray-800">
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex rounded-lg bg-gray-800 p-1">
                  {(['3D', '2D', 'AR'] as Mode[]).map(m => (
                    <button
                      key={m}
                      onClick={() => setMode(m)}
                      className={`px-4 py-2 text-sm rounded-md transition-all ${
                        mode === m 
                          ? 'bg-gray-700 text-white' 
                          : 'text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      {m === '3D' && <Boxes className="inline h-4 w-4 mr-2" />}
                      {m === '2D' && <Layers className="inline h-4 w-4 mr-2" />}
                      {m === 'AR' && <Eye className="inline h-4 w-4 mr-2" />}
                      {m}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:bg-gray-800">
                    <Copy className="h-4 w-4 mr-2" />Kopiuj
                  </Button>
                  <Button variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:bg-gray-800">
                    <ImageIcon className="h-4 w-4 mr-2" />PNG
                  </Button>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Download className="h-4 w-4 mr-2" />PDF
                  </Button>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="rounded-2xl border border-gray-800 relative overflow-hidden bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-gray-900">
                <div className="aspect-[16/9]">
                  {mode === '3D' && (
                    <div className="h-full">
                      <Suspense fallback={<div className="flex items-center justify-center h-full text-gray-400">Ładowanie podglądu 3D...</div>}>
                        <ThreePreview w={dims.width} h={dims.height} d={dims.depth} />
                      </Suspense>
                    </div>
                  )}
                  {mode === '2D' && (
                    <TechDrawing 
                      width={dims.width} 
                      height={dims.height} 
                      depth={dims.depth} 
                      thickness={dims.thickness}
                    />
                  )}
                  {mode === 'AR' && (
                    <div className="h-full flex items-center justify-center text-gray-400">
                      <div className="text-center">
                        <Eye className="h-12 w-12 mx-auto mb-3 text-gray-600" />
                        <p>Podgląd AR będzie dostępny jako link/QR do modelu</p>
                        <p className="text-sm mt-2 text-gray-500">(placeholder UI)</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Miniatury / historia */}
          <Card className="backdrop-blur bg-gray-900/70 border-gray-800">
            <div className="p-4 border-b border-gray-800">
              <div className="text-lg font-medium text-white">Historia projektów</div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Array.from({length:4}).map((_,i)=>(
                  <div key={i} className="aspect-square rounded-xl border border-gray-800 bg-gray-800/30 animate-pulse" />
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function FormNumber({label, value, onChange}:{label:string; value:number; onChange:(v:number)=>void}) {
  return (
    <div>
      <div className="text-xs mb-1 text-gray-400">{label}</div>
      <input 
        type="number" 
        value={value} 
        onChange={e=>onChange(Number(e.target.value))}
        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:border-blue-500"
      />
    </div>
  );
}

function FormSelect({label, value, onChange, items}:{label:string; value:string; onChange:(v:string)=>void; items:string[]}) {
  return (
    <div>
      <div className="text-xs mb-1 text-gray-400">{label}</div>
      <select 
        value={value} 
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:border-blue-500"
      >
        {items.map(it => <option key={it} value={it}>{it}</option>)}
      </select>
    </div>
  );
}

function TechDrawing({width, height, depth, thickness}:{width:number;height:number;depth:number;thickness:number;}) {
  // prosty rysunek izometryczny + wymiary
  return (
    <div className="h-full w-full grid place-items-center p-6">
      <svg width="100%" height="100%" viewBox="0 0 800 400" className="text-gray-400">
        <rect x="100" y="80" width="420" height="220" fill="none" stroke="currentColor" strokeWidth="2" />
        <line x1="520" y1="80" x2="650" y2="40" stroke="currentColor" strokeWidth="2" />
        <line x1="520" y1="300" x2="650" y2="260" stroke="currentColor" strokeWidth="2" />
        <line x1="650" y1="40" x2="650" y2="260" stroke="currentColor" strokeWidth="2" />
        <text x="110" y="75" fontSize="12" fill="currentColor">Grubość: {thickness} mm</text>
        <text x="250" y="330" fontSize="12" fill="currentColor">Szerokość: {width} mm</text>
        <text x="540" y="50"  fontSize="12" fill="currentColor">Głębokość: {depth} mm</text>
        <text x="540" y="280" fontSize="12" fill="currentColor">Wysokość: {height} mm</text>
      </svg>
    </div>
  );
}
